import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { 
  ArrowLeft, Shield, AlertTriangle, AlertOctagon, Copy, 
  Activity, ArrowRightLeft, Route as RouteIcon, TrendingUp, TrendingDown,
  DollarSign, BarChart3, Wallet, Lock, Unlock, ChevronDown, ChevronUp, FileText, AlertCircle,
  Link as LinkIcon, CheckCircle, Download
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";

// Copy to clipboard helper
const copyToClipboard = (text, label) => {
  navigator.clipboard.writeText(text);
  toast.success(`${label} скопирован`);
};

// Trust Score component
const TrustScore = ({ score }) => {
  const getColor = () => {
    if (score >= 70) return "text-emerald-600";
    if (score >= 40) return "text-amber-600";
    return "text-rose-600";
  };
  
  const getBgColor = () => {
    if (score >= 70) return "bg-emerald-100 border-emerald-300";
    if (score >= 40) return "bg-amber-100 border-amber-300";
    return "bg-rose-100 border-rose-300";
  };
  
  return (
    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${getBgColor()}`}>
      <span className="text-xs text-slate-600">Доверие:</span>
      <span className={`text-lg font-bold ${getColor()}`}>{score || 0}</span>
      <span className="text-xs text-slate-500">/100</span>
    </div>
  );
};

const RiskBadge = ({ level }) => {
  const config = {
    low: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", icon: Shield, label: "Низкий риск" },
    medium: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", icon: AlertTriangle, label: "Средний риск" },
    high: { bg: "bg-rose-50", text: "text-rose-700", border: "border-rose-200", icon: AlertOctagon, label: "Высокий риск" }
  };
  
  const { bg, text, border, icon: Icon, label } = config[level] || config.low;
  
  return (
    <Badge className={`${bg} ${text} ${border} border px-2 py-0.5 text-xs font-medium`} data-testid={`risk-badge-${level}`}>
      <Icon className="w-3 h-3 mr-1" />
      {label}
    </Badge>
  );
};

const TradeTypeBadge = ({ type }) => {
  const typeConfig = {
    buy_crypto: { bg: "bg-green-50", text: "text-green-700", label: "Покупка крипто" },
    sell_crypto: { bg: "bg-blue-50", text: "text-blue-700", label: "Продажа крипто" },
    buy_stock: { bg: "bg-emerald-50", text: "text-emerald-700", label: "Покупка акций" },
    sell_stock: { bg: "bg-cyan-50", text: "text-cyan-700", label: "Продажа акций" },
    swap: { bg: "bg-purple-50", text: "text-purple-700", label: "Обмен" },
    stake: { bg: "bg-amber-50", text: "text-amber-700", label: "Стейкинг" },
    unstake: { bg: "bg-orange-50", text: "text-orange-700", label: "Анстейкинг" },
    airdrop: { bg: "bg-pink-50", text: "text-pink-700", label: "Аирдроп" },
    transfer_in: { bg: "bg-teal-50", text: "text-teal-700", label: "Вход" },
    transfer_out: { bg: "bg-slate-100", text: "text-slate-700", label: "Выход" },
    deposit: { bg: "bg-green-50", text: "text-green-700", label: "Пополнение" },
    withdrawal: { bg: "bg-rose-50", text: "text-rose-700", label: "Вывод" },
    notification: { bg: "bg-indigo-50", text: "text-indigo-700", label: "Уведомление" },
  };
  
  const config = typeConfig[type] || { bg: "bg-slate-50", text: "text-slate-600", label: type };
  
  return (
    <Badge className={`${config.bg} ${config.text} border-0 text-xs`}>
      {config.label}
    </Badge>
  );
};

// Mini balance chart component
const BalanceChart = ({ history }) => {
  if (!history || history.length < 2) return null;
  
  const balances = history.map(h => h.balance_after || 0);
  const maxBalance = Math.max(...balances);
  const minBalance = Math.min(...balances);
  const range = maxBalance - minBalance || 1;
  
  const points = balances.map((b, i) => {
    const x = (i / (balances.length - 1)) * 100;
    const y = 100 - ((b - minBalance) / range) * 100;
    return `${x},${y}`;
  }).join(' ');
  
  const isPositive = balances[balances.length - 1] >= balances[0];
  const color = isPositive ? "#10B981" : "#EF4444";
  
  return (
    <div className="w-full h-full">
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-full">
        <defs>
          <linearGradient id={`gradient-${isPositive ? 'up' : 'down'}`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={color} stopOpacity="0.3" />
            <stop offset="100%" stopColor={color} stopOpacity="0.05" />
          </linearGradient>
        </defs>
        <polygon 
          points={`0,100 ${points} 100,100`} 
          fill={`url(#gradient-${isPositive ? 'up' : 'down'})`}
        />
        <polyline
          points={points}
          fill="none"
          stroke={color}
          strokeWidth="2"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
    </div>
  );
};

// Expandable Trade Card component
const TradeCard = ({ trade, idx, hideBalance, hideTradingBalance }) => {
  const [expanded, setExpanded] = useState(false);
  const hasThreat = trade.detailed_description && trade.risk_indicator === "high";
  
  return (
    <Card 
      className={`card-hover cursor-pointer transition-all ${hasThreat ? 'border-rose-300 bg-rose-50/30' : ''}`}
      data-testid={`trade-${idx}`}
      onClick={() => setExpanded(!expanded)}
    >
      <CardContent className="p-3 sm:p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
              {/* Threat Icon */}
              {hasThreat && (
                <div className="flex items-center justify-center w-6 h-6 bg-rose-100 border border-rose-300 rounded-full">
                  <AlertCircle className="w-4 h-4 text-rose-600" />
                </div>
              )}
              <TradeTypeBadge type={trade.type} />
              <span className="text-xs text-slate-500">
                {trade.platform}
              </span>
              <RiskBadge level={trade.risk_indicator} />
            </div>
            <p className="text-sm text-slate-900 font-medium mb-1 break-words">
              {trade.description}
            </p>
            {!hideTradingBalance && (
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <span className="flex items-center gap-1">
                  <Wallet className="w-3 h-3" />
                  Баланс: ${trade.balance_after?.toLocaleString()}
                </span>
              </div>
            )}
          </div>
          <div className="text-right shrink-0 flex items-start gap-2">
            <div>
              <div className={`text-base sm:text-lg font-bold ${trade.profit_loss >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                {trade.profit_loss >= 0 ? '+' : ''}{trade.profit_loss?.toLocaleString(undefined, {maximumFractionDigits: 2})}
              </div>
              <div className="text-xs text-slate-500">USD</div>
            </div>
            {trade.detailed_description && (
              expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />
            )}
          </div>
        </div>
        
        {/* Expandable details */}
        {expanded && trade.detailed_description && (
          <div className="mt-3 pt-3 border-t border-rose-200">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-rose-500 mt-0.5 shrink-0" />
              <p className="text-sm text-slate-700 whitespace-pre-wrap">
                {trade.detailed_description}
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const AddressPage = () => {
  const { addressId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retrying, setRetrying] = useState(false);

  const fetchData = async (isRetry = false) => {
    if (isRetry) setRetrying(true);
    else setLoading(true);
    
    // Clean the addressId - trim whitespace and remove any trailing slashes
    const cleanId = (addressId || "").trim().replace(/\/+$/, "");
    // Add cache-buster to prevent aggressive mobile caching
    const url = `${API}/address/${encodeURIComponent(cleanId)}?_t=${Date.now()}`;
    
    try {
      // Try with axios first
      const response = await axios.get(url, { 
        timeout: 15000,
        headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
      });
      setData(response.data);
      setError(null);
    } catch (err) {
      console.error("Fetch error:", err);
      
      // If axios fails (e.g., CORS/network), try native fetch as fallback
      try {
        const fallbackResp = await fetch(url, {
          method: 'GET',
          headers: { 'Accept': 'application/json', 'Cache-Control': 'no-cache' },
        });
        if (fallbackResp.ok) {
          const jsonData = await fallbackResp.json();
          setData(jsonData);
          setError(null);
          return;
        }
        const errBody = await fallbackResp.json().catch(() => ({}));
        setError({
          message: errBody.detail || `HTTP ${fallbackResp.status}`,
          status: fallbackResp.status,
          url: url,
          code: "FETCH_FALLBACK",
        });
      } catch (fetchErr) {
        const status = err.response?.status || "no response";
        const detail = err.response?.data?.detail || err.message || "Unknown error";
        setError({
          message: detail,
          status: status,
          url: url,
          code: err.code || "UNKNOWN",
        });
      }
    } finally {
      setLoading(false);
      setRetrying(false);
    }
  };

  useEffect(() => {
    if (addressId) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressId]);

  const copyAddress = () => {
    if (data?.address?.address) {
      try {
        navigator.clipboard.writeText(data.address.address);
        toast.success("Адрес скопирован");
      } catch (e) {
        // Fallback for mobile
        const textArea = document.createElement("textarea");
        textArea.value = data.address.address;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
        toast.success("Адрес скопирован");
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white p-4" data-testid="address-error-page">
        <div className="text-center max-w-md">
          <div className="w-14 h-14 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-7 h-7 text-rose-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">Адрес не найден</h2>
          <p className="text-slate-500 mb-6 font-mono text-xs break-all">ID: {addressId}</p>

          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <Button 
              onClick={() => fetchData(true)} 
              disabled={retrying}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="retry-btn"
            >
              {retrying ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
              ) : (
                <Activity className="w-4 h-4 mr-2" />
              )}
              Повторить
            </Button>
            <Button onClick={() => navigate("/")} variant="outline" data-testid="go-home-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />
              На главную
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const { address, transactions, routes } = data;
  const tradingHistory = address.trading_history || [];
  const isBlocked = address.is_blocked || false;

  // Calculate stats
  const totalProfit = tradingHistory.reduce((sum, t) => sum + (t.profit_loss || 0), 0);
  const profitableTrades = tradingHistory.filter(t => t.profit_loss > 0).length;
  const lossTrades = tradingHistory.filter(t => t.profit_loss < 0).length;
  
  // Calculate change percentage
  const firstBalance = tradingHistory[0]?.balance_after || 0;
  const lastBalance = tradingHistory[tradingHistory.length - 1]?.balance_after || address.total_balance || 0;
  const changePercent = firstBalance > 0 ? ((lastBalance - firstBalance) / firstBalance * 100) : 0;

  return (
    <div className="min-h-screen bg-white" data-testid="address-page">
      {/* Header */}
      <header className="header-blur border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/")} data-testid="back-btn" className="px-2 sm:px-3">
              <ArrowLeft className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Назад</span>
            </Button>
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-slate-900 rounded-lg flex items-center justify-center">
                <Activity className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
              </div>
              <span className="text-lg sm:text-xl font-bold text-slate-900">ChainScope</span>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
        {/* Address Header - Mobile optimized */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-6">
          {/* Left side - Address info */}
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <Badge variant="outline" className="font-mono text-xs">
                {address.network}
              </Badge>
              <RiskBadge level={address.risk_level} />
              {address.total_balance && !address.hide_balance && (
                <Badge className="bg-blue-50 text-blue-700 border-blue-200 border px-2 py-0.5 text-xs">
                  <DollarSign className="w-3 h-3 mr-1" />
                  {address.total_balance.toLocaleString()} USD
                </Badge>
              )}
              {isBlocked && (
                <Badge className="bg-rose-100 text-rose-700 border-rose-300 border px-2 py-0.5 text-xs">
                  <Lock className="w-3 h-3 mr-1" />
                  Заблокирован
                </Badge>
              )}
            </div>
            
            <div className="flex items-center gap-2 mb-2">
              <h1 className="font-mono text-sm sm:text-base text-slate-900 break-all" data-testid="address-value">
                {address.address}
              </h1>
              <Button variant="ghost" size="sm" onClick={copyAddress} data-testid="copy-btn" className="shrink-0 p-1">
                <Copy className="w-4 h-4" />
              </Button>
            </div>

            {address.label && (
              <p className="text-slate-600 text-sm sm:text-base">{address.label}</p>
            )}

            {address.tags && address.tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {address.tags.filter(tag => tag !== 'auto-generated' && tag !== 'trading-history').map((tag, idx) => (
                  <Badge key={idx} variant="secondary" className="text-xs">
                    {tag}
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Right side - Balance Chart */}
          {tradingHistory.length > 1 && !address.hide_balance && (
            <div className="w-full lg:w-80 h-24 sm:h-28 bg-slate-50 rounded-xl border border-slate-200 p-3 relative overflow-hidden">
              <div className="absolute top-2 left-3 z-10">
                <p className="text-xs text-slate-500">Динамика баланса</p>
                <div className="flex items-center gap-2">
                  <span className="text-lg sm:text-xl font-bold text-slate-900">
                    ${lastBalance.toLocaleString(undefined, {maximumFractionDigits: 0})}
                  </span>
                  <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${changePercent >= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                    {changePercent >= 0 ? '+' : ''}{changePercent.toFixed(1)}%
                  </span>
                </div>
              </div>
              <div className="absolute inset-0 pt-10">
                <BalanceChart history={tradingHistory} />
              </div>
            </div>
          )}
        </div>

        {/* Stats Cards - Mobile optimized */}
        {tradingHistory.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 mb-6">
            <Card className="bg-slate-50">
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-lg flex items-center justify-center shrink-0">
                    <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-slate-500">Всего операций</p>
                    <p className="text-lg sm:text-xl font-bold text-slate-900">{tradingHistory.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-emerald-50">
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-emerald-100 rounded-lg flex items-center justify-center shrink-0">
                    <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-slate-500">Прибыльных</p>
                    <p className="text-lg sm:text-xl font-bold text-emerald-600">{profitableTrades}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-rose-50">
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-rose-100 rounded-lg flex items-center justify-center shrink-0">
                    <TrendingDown className="w-4 h-4 sm:w-5 sm:h-5 text-rose-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-slate-500">Убыточных</p>
                    <p className="text-lg sm:text-xl font-bold text-rose-600">{lossTrades}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className={totalProfit >= 0 ? "bg-emerald-50" : "bg-rose-50"}>
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center shrink-0 ${totalProfit >= 0 ? 'bg-emerald-100' : 'bg-rose-100'}`}>
                    <DollarSign className={`w-4 h-4 sm:w-5 sm:h-5 ${totalProfit >= 0 ? 'text-emerald-600' : 'text-rose-600'}`} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-slate-500">Общий P&L</p>
                    {address.hide_balance ? (
                      <p className="text-lg sm:text-xl font-bold text-slate-400">***</p>
                    ) : (
                      <p className={`text-lg sm:text-xl font-bold truncate ${totalProfit >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                        {totalProfit >= 0 ? '+' : ''}{totalProfit.toLocaleString(undefined, {maximumFractionDigits: 2})}
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Download Report Button */}
        {tradingHistory.length > 0 && (
          <div className="flex justify-center sm:justify-end mb-6">
            <Button
              onClick={async () => {
                try {
                  toast.success("PDF-отчёт загружается...");
                  const res = await fetch(`${API}/address/${addressId}/report`);
                  if (!res.ok) throw new Error(`HTTP ${res.status}`);
                  const blob = await res.blob();
                  const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `ChainScope_Report_${addressId}.pdf`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  window.URL.revokeObjectURL(url);
                } catch (err) {
                  toast.error("Ошибка скачивания отчёта");
                }
              }}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="download-report-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Скачать PDF-отчёт
            </Button>
          </div>
        )}

        {/* Trust Score & Risk Pie Chart */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          {/* Trust Score Card */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 mb-1">Скоринг доверия</p>
                  <div className="flex items-center gap-3">
                    <span className={`text-4xl font-bold ${
                      (address.trust_score || 0) >= 70 ? 'text-emerald-600' :
                      (address.trust_score || 0) >= 40 ? 'text-amber-600' : 'text-rose-600'
                    }`}>
                      {address.trust_score || 0}
                    </span>
                    <span className="text-slate-400 text-xl">/100</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    {(address.trust_score || 0) >= 70 ? 'Высокий уровень доверия' :
                     (address.trust_score || 0) >= 40 ? 'Средний уровень доверия' : 'Низкий уровень доверия'}
                  </p>
                </div>
                <div className="w-20 h-20">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={[
                          { value: address.trust_score || 0 },
                          { value: 100 - (address.trust_score || 0) }
                        ]}
                        innerRadius={25}
                        outerRadius={35}
                        startAngle={90}
                        endAngle={-270}
                        dataKey="value"
                      >
                        <Cell fill={(address.trust_score || 0) >= 70 ? '#10b981' : (address.trust_score || 0) >= 40 ? '#f59e0b' : '#ef4444'} />
                        <Cell fill="#e2e8f0" />
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Risk Distribution Pie Chart */}
          {tradingHistory.length > 0 && (
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-slate-500 mb-2">Распределение рисков</p>
                <div className="flex items-center gap-4">
                  <div className="w-24 h-24">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={[
                            { name: 'Низкий', value: tradingHistory.filter(t => t.risk_indicator === 'low').length, color: '#10b981' },
                            { name: 'Средний', value: tradingHistory.filter(t => t.risk_indicator === 'medium').length, color: '#f59e0b' },
                            { name: 'Высокий', value: tradingHistory.filter(t => t.risk_indicator === 'high').length, color: '#ef4444' }
                          ].filter(d => d.value > 0)}
                          innerRadius={20}
                          outerRadius={40}
                          dataKey="value"
                        >
                          {[
                            { name: 'Низкий', value: tradingHistory.filter(t => t.risk_indicator === 'low').length, color: '#10b981' },
                            { name: 'Средний', value: tradingHistory.filter(t => t.risk_indicator === 'medium').length, color: '#f59e0b' },
                            { name: 'Высокий', value: tradingHistory.filter(t => t.risk_indicator === 'high').length, color: '#ef4444' }
                          ].filter(d => d.value > 0).map((entry, index) => (
                            <Cell key={index} fill={entry.color} />
                          ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex-1 space-y-1.5">
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full bg-emerald-500"></span>
                        Низкий
                      </span>
                      <span className="font-medium">{tradingHistory.filter(t => t.risk_indicator === 'low').length}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full bg-amber-500"></span>
                        Средний
                      </span>
                      <span className="font-medium">{tradingHistory.filter(t => t.risk_indicator === 'medium').length}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full bg-rose-500"></span>
                        Высокий
                      </span>
                      <span className="font-medium">{tradingHistory.filter(t => t.risk_indicator === 'high').length}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Blocked Warning */}
        {isBlocked && (
          <Card className="mb-6 border-rose-200 bg-rose-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-rose-100 rounded-full flex items-center justify-center shrink-0">
                  <Lock className="w-5 h-5 text-rose-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-rose-700">Адрес заблокирован</h3>
                  <p className="text-sm text-rose-600">
                    Данный адрес помечен как заблокированный. Активы могут быть заморожены.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Risk Analysis Section */}
        {address.risk_analysis && (
          <Card className={`mb-6 ${
            address.risk_level === 'high' ? 'border-rose-200 bg-rose-50/50' :
            address.risk_level === 'medium' ? 'border-amber-200 bg-amber-50/50' :
            'border-emerald-200 bg-emerald-50/50'
          }`}>
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                  address.risk_level === 'high' ? 'bg-rose-100' :
                  address.risk_level === 'medium' ? 'bg-amber-100' :
                  'bg-emerald-100'
                }`}>
                  <AlertCircle className={`w-5 h-5 ${
                    address.risk_level === 'high' ? 'text-rose-600' :
                    address.risk_level === 'medium' ? 'text-amber-600' :
                    'text-emerald-600'
                  }`} />
                </div>
                <div>
                  <h3 className={`font-semibold mb-2 ${
                    address.risk_level === 'high' ? 'text-rose-700' :
                    address.risk_level === 'medium' ? 'text-amber-700' :
                    'text-emerald-700'
                  }`}>
                    Анализ рисков адреса
                  </h3>
                  <p className="text-sm text-slate-600 whitespace-pre-wrap">
                    {address.risk_analysis}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs - Mobile optimized */}
        <Tabs defaultValue={tradingHistory.length > 0 ? "history" : "transactions"} className="space-y-4">
          <TabsList className="bg-slate-100 w-full sm:w-auto flex">
            {tradingHistory.length > 0 && (
              <TabsTrigger value="history" data-testid="tab-history" className="flex-1 sm:flex-initial text-xs sm:text-sm">
                <BarChart3 className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">История торговли</span>
                <span className="sm:hidden">История</span>
                <span className="ml-1">({tradingHistory.length})</span>
              </TabsTrigger>
            )}
            <TabsTrigger value="transactions" data-testid="tab-transactions" className="flex-1 sm:flex-initial text-xs sm:text-sm">
              <ArrowRightLeft className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Транзакции</span>
              <span className="sm:hidden">Тр-ции</span>
              <span className="ml-1">({transactions.length})</span>
            </TabsTrigger>
            <TabsTrigger value="routes" data-testid="tab-routes" className="flex-1 sm:flex-initial text-xs sm:text-sm">
              <RouteIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Маршруты</span>
              <span className="sm:hidden">Марш.</span>
              <span className="ml-1">({routes.length})</span>
            </TabsTrigger>
          </TabsList>

          {/* Trading History Tab */}
          {tradingHistory.length > 0 && (
            <TabsContent value="history">
              <div className="space-y-2 sm:space-y-3">
                {[...tradingHistory].reverse().map((trade, idx) => (
                  <TradeCard 
                    key={idx} 
                    trade={trade} 
                    idx={idx} 
                    hideBalance={address.hide_balance}
                    hideTradingBalance={address.hide_trading_balance}
                  />
                ))}
              </div>
            </TabsContent>
          )}

          {/* Transactions Tab */}
          <TabsContent value="transactions">
            {transactions.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-slate-500">
                  Транзакции не найдены
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2 sm:space-y-3">
                {transactions.map((tx) => (
                  <Card key={tx.tx_id} className="card-hover" data-testid={`tx-${tx.tx_id}`}>
                    <CardContent className="p-3 sm:p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
                            {tx.trade_type && <TradeTypeBadge type={tx.trade_type} />}
                            <RiskBadge level={tx.risk_level} />
                            {tx.platform && (
                              <Badge variant="outline" className="text-xs">{tx.platform}</Badge>
                            )}
                          </div>
                          {tx.notes && (
                            <p className="text-sm text-slate-900 mb-1.5 break-words">{tx.notes}</p>
                          )}
                          <div className="flex items-center gap-1.5 text-xs sm:text-sm">
                            <span className="font-mono text-slate-600 truncate max-w-[80px] sm:max-w-[150px]">
                              {tx.from_address.slice(0, 8)}...
                            </span>
                            <ArrowRightLeft className="w-3 h-3 sm:w-4 sm:h-4 text-slate-400 shrink-0" />
                            <span className="font-mono text-slate-600 truncate max-w-[80px] sm:max-w-[150px]">
                              {tx.to_address.slice(0, 8)}...
                            </span>
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="font-semibold text-slate-900 text-sm sm:text-base">
                            {tx.amount} {tx.token}
                          </div>
                          {tx.balance_after && !address.hide_trading_balance && (
                            <div className="text-xs text-slate-500">
                              Баланс: ${tx.balance_after.toLocaleString()}
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Routes Tab */}
          <TabsContent value="routes">
            {routes.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-slate-500">
                  Маршруты не найдены
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                {routes.map((route) => (
                  <Link key={route.route_id} to={`/route/${route.route_id}`}>
                    <Card className="card-hover cursor-pointer h-full" data-testid={`route-${route.route_id}`}>
                      <CardHeader className="pb-2 p-3 sm:p-4">
                        <CardTitle className="text-sm sm:text-base flex items-center gap-2">
                          <RouteIcon className="w-4 h-4 text-slate-500 shrink-0" />
                          <span className="truncate">{route.name}</span>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-3 sm:p-4 pt-0">
                        <p className="text-xs sm:text-sm text-slate-500 mb-2 line-clamp-2">
                          {route.description || "Без описания"}
                        </p>
                        <div className="flex items-center gap-3 text-xs text-slate-500">
                          <span>{route.nodes?.length || 0} узлов</span>
                          <span>{route.edges?.length || 0} связей</span>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Notes */}
        {address.notes && (
          <Card className="mt-6">
            <CardHeader className="p-3 sm:p-4 pb-2">
              <CardTitle className="text-sm sm:text-base">Заметки</CardTitle>
            </CardHeader>
            <CardContent className="p-3 sm:p-4 pt-0">
              <p className="text-xs sm:text-sm text-slate-600">{address.notes}</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AddressPage;
